// src/contexts/AuthContext.tsx
"use client";

import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { useUser, useAuth } from "@clerk/nextjs";

// 型定義
interface AuthContextType {
  clerkUser: ReturnType<typeof useUser>["user"] | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Clerk関連
  const { user, isLoaded } = useUser();
  const { userId } = useAuth();

  // Clerkユーザー情報を取得できたか
  const clerkUser = (isLoaded && userId && user) ? user : null;

  // ローディング状態はClerkのみ
  const isLoading = !isLoaded || !userId || !user;

  // useMemoで値をキャッシュ
  const contextValue = useMemo(
    () => ({
      clerkUser,
      isLoading,
    }),
    [clerkUser, isLoading]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// カスタムフック（必ずProvider配下で利用）
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
